import {ButtonGroup, ButtonProps, TabProps, Tabs, closeCurrentDialog} from 'platform/components';
import {Box, VStack} from 'platform/foundation';

import {useState} from 'react';

import {head, isNil, isNotNil} from 'ramda';

import {RecordIdHttpModel} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  RECORD_AUDIT_PAIR_GRID_CODES,
  RecordAuditPairResourceId,
  getRecordAuditPairResourceLabel,
  isRecordAuditPairResourceId,
  useLicensedRecordAuditPairResources,
} from '@dms/shared';

import {buildArray, getSafely} from 'shared/web';

interface RecordAuditPairRecordDialogProps {
  initialResourceId?: RecordAuditPairResourceId;
  onConfirm: (record: RecordIdHttpModel) => void;
  onClear?: () => void;
}

export function RecordAuditPairRecordDialog(props: RecordAuditPairRecordDialogProps) {
  const licensedResourceIds = useLicensedRecordAuditPairResources();

  const [selectedTabId, setSelectedTabId] = useState<RecordAuditPairResourceId>(
    props.initialResourceId ?? licensedResourceIds[0]
  );
  // The selected tab may fall outside the licensed set — an existing pairing to an
  // unlicensed resource, or a licence change while the dialog is open (ACL refetch).
  // Derive the effective tab so it always lands on a licensed one (the list is never
  // empty; VEHICLE is core and always licensed).
  const activeTabId = licensedResourceIds.includes(selectedTabId)
    ? selectedTabId
    : licensedResourceIds[0];
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const handleTabChange = (id: string) => {
    if (!isRecordAuditPairResourceId(id)) {
      return;
    }
    setSelectedTabId(id);
    setSelectedRowId(null);
  };

  const handleConfirm = () => {
    if (isNil(selectedRowId)) {
      return;
    }
    props.onConfirm({resourceId: activeTabId, id: selectedRowId});
    closeCurrentDialog();
  };

  const tabs: TabProps[] = licensedResourceIds.map((id) => ({
    id,
    title: getRecordAuditPairResourceLabel(id),
    content: (
      <Box height="100%">
        <DmsDataGrid
          gridCode={getSafely(RECORD_AUDIT_PAIR_GRID_CODES, id) ?? ''}
          onRowSelectionChange={(rows) => setSelectedRowId(head(rows)?.id ?? null)}
        />
      </Box>
    ),
  }));

  const handleClear = () => {
    props.onClear?.();
    closeCurrentDialog();
  };

  const isPaired = isNotNil(props.initialResourceId);

  return (
    <VStack height="100%" spacing={4}>
      <Box flex={1} minHeight={0}>
        <Tabs
          isFullHeight
          variant="condensed"
          tabs={tabs}
          activeTabId={activeTabId}
          onChange={handleTabChange}
          lazyBehavior="unmount"
          isLazy
        />
      </Box>
      <ButtonGroup
        align="right"
        buttons={buildArray<ButtonProps>()
          .when(isPaired && isNotNil(props.onClear), {
            title: i18n.t('general.actions.clear'),
            onClick: handleClear,
            variant: 'ghostLink',
          })
          .add({
            title: i18n.t('general.actions.cancel'),
            onClick: closeCurrentDialog,
            variant: 'secondary',
          })
          .add({
            title: i18n.t('general.actions.save'),
            onClick: handleConfirm,
            isDisabled: isNil(selectedRowId),
          })}
      />
    </VStack>
  );
}
